import * as React from "react"
import { gql } from "@apollo/client"
import {
  Box,
  Center,
  Heading,
  HStack,
  Image,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useBreakpointValue,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react"
import Head from "next/head"
import { useRouter } from "next/router"

import { nestedPaginate } from "lib/apollo/helpers"
import { EventDetailFragment, EventPersonFragment, useGetEventQuery } from "lib/graphql"
import { transformImage } from "lib/helpers/utils"
import { EventAttendees } from "components/EventAttendees"
import { EventInfo } from "components/EventInfo"
import { Layout } from "components/Layout"
import { Limiter } from "components/Limiter"
import { Modal } from "components/Modal"
import { SanitizedContent } from "components/SanitizedContent"

const _ = gql`
  fragment EventPerson on EventPerson {
    id
    name
    bio
    imageUrl
    type
  }

  fragment EventDetail on Event {
    id
    name
    slug
    date
    time
    location
    content
    image
    type
    region
    categories
    disabilityProvisions
    ticketLink
    maxAttendees
    isRestrictedToRegion
    isAttendable
    isPublished
    createdAt
    updatedAt
    ticketLink
    isAttending
    attendeeCount
    attendees(take: $take, orderBy: $orderBy, where: $where, skip: $skip) {
      items {
        ...EventAttendee
      }
      count
    }
    eventPeople {
      ...EventPerson
    }
  }

  query GetEvent(
    $slug: String!
    $take: Int
    $orderBy: [UserEventOrderByWithRelationInput!]
    $where: UserEventWhereInput
    $skip: Int
  ) {
    event(slug: $slug) {
      ...EventDetail
    }
  }
`

const TAKE = 10

export default function EventDetail() {
  const router = useRouter()
  const isMobile = useBreakpointValue({ base: true, md: false })
  const colorBlackWhite = useColorModeValue("black", "white")
  const slug = router.query.slug as string

  const { data, loading, fetchMore, refetch } = useGetEventQuery({ skip: !slug, variables: { slug, take: TAKE } })

  const event = data?.event

  const handleFetchMore = () => {
    if (!event) return
    return fetchMore({
      variables: {
        skip: event.attendees.items.length,
        take: event.attendees.count - event.attendees.items.length,
      },
      updateQuery: nestedPaginate("event", "attendees"),
    })
  }

  if (loading)
    return (
      <Center h="100vh" w="100vw">
        <Spinner />
      </Center>
    )
  if (!event)
    return (
      <Center h="100vh" w="100vw">
        Geen te-doen gevonden
      </Center>
    )
  if (isMobile) return <EventDetailMobile event={event} onFetchMore={handleFetchMore} refetch={refetch} />

  return (
    <>
      <Head>
        <title>Kunstbende - {event?.name}</title>
      </Head>
      <Limiter mt={16} mb={56}>
        <Stack spacing={8}>
          <Box>
            <Heading
              fontSize={{ base: 32, md: 60 }}
              fontWeight={900}
              lineHeight={{ base: "shorter", md: "short" }}
              px={0}
              textAlign="left"
              color={colorBlackWhite}
              w={["100%", "75%"]}
            >
              {event.name}
            </Heading>
            <Text fontSize="2xl" lineHeight={{ base: "shorter", md: "short" }} color="gray.400" maxW="650px">
              {event.type} / {event.region} /{" "}
              {event.categories?.reduce((acc, i) => acc + i + ", ", "").slice(0, -2)}
            </Text>
          </Box>
          <HStack spacing={32} alignItems="flex-start">
            <Stack flex={5} spacing={8}>
              <Image
                src={event?.image ? transformImage(event?.image, "h_850") : "/event-placeholder.png"}
                h="350px"
                w="100%"
                objectFit="cover"
                objectPosition="center"
                alt="evenement afbeelding"
                borderRadius="xl"
                border="none"
              />
              <SanitizedContent content={event.content} />
            </Stack>
            <Stack flex={3} spacing={10}>
              <EventInfo event={event} refetch={refetch} />
              <EventAttendees event={event} onShowMore={handleFetchMore} count={event.attendeeCount} />
            </Stack>
          </HStack>
          {event.eventPeople.filter((person) => person.type === "SPEAKER").length > 0 && (
            <Stack spacing={8} pt={12}>
              <Heading fontWeight="black" fontSize="3xl">
                Speakers
              </Heading>
              <SimpleGrid columns={{ base: 4, md: 4 }} spacing={16} minH="150px" w="100%">
                {event.eventPeople
                  .filter((person) => person.type === "SPEAKER")
                  .map((person) => (
                    <Person key={person.id} person={person} />
                  ))}
              </SimpleGrid>
            </Stack>
          )}
          {event.eventPeople.filter((person) => person.type === "TEACHER").length > 0 && (
            <Stack spacing={8} pt={12}>
              <Heading fontWeight="black" fontSize="3xl">
                Docent
              </Heading>
              <SimpleGrid columns={{ base: 4, md: 4 }} spacing={16} minH="150px" w="100%">
                {event.eventPeople
                  .filter((person) => person.type === "TEACHER")
                  .map((person) => (
                    <Person key={person.id} person={person} />
                  ))}
              </SimpleGrid>
            </Stack>
          )}
          {event.eventPeople.filter((person) => person.type === "HOST").length > 0 && (
            <Stack spacing={8} pt={12}>
              <Heading fontWeight="black" fontSize="3xl">
                Hosts
              </Heading>
              <SimpleGrid columns={{ base: 4, md: 4 }} spacing={16} minH="150px">
                {event.eventPeople
                  .filter((person) => person.type === "HOST")
                  .map((person) => (
                    <Person key={person.id} person={person} />
                  ))}
              </SimpleGrid>
            </Stack>
          )}
        </Stack>
      </Limiter>
    </>
  )
}

const EventDetailMobile: React.FC<{
  event: EventDetailFragment
  onFetchMore: () => void
  refetch: () => void
}> = ({ event, onFetchMore, refetch }) => {
  return (
    <>
      <Head>
        <title>Kunstbende - {event?.name}</title>
      </Head>
      <Limiter my={12}>
        <Stack spacing={4}>
          <Image
            src={event?.image ? transformImage(event?.image, "h_800") : "/event-placeholder.png"}
            h="350px"
            w="100%"
            objectFit="cover"
            objectPosition="center"
            alt="evenement afbeelding"
            borderRadius="lg"
            border="none"
          />
          <Heading
            fontSize={{ base: 32, md: 60 }}
            fontWeight={900}
            lineHeight={{ base: "shorter", md: "short" }}
            px={0}
            textAlign="left"
          >
            {event.name}
          </Heading>
          <Text fontSize="lg" color="gray.500" maxW="650px">
            {event.type} / {event.region} /{" "}
            {event.categories?.reduce((acc, i) => acc + i + ", ", "").slice(0, -2)}
          </Text>
          <EventInfo event={event} refetch={refetch} />
          <SanitizedContent content={event.content} />
          {event.eventPeople.filter((person) => person.type === "SPEAKER").length > 0 && (
            <Stack pt={16} spacing={8}>
              <Heading fontWeight="black" fontSize="3xl">
                Speakers
              </Heading>
              <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={16} minH="150px">
                {event.eventPeople
                  .filter((person) => person.type === "SPEAKER")
                  .map((person) => (
                    <Person key={person.id} person={person} />
                  ))}
              </SimpleGrid>
            </Stack>
          )}
          {event.eventPeople.filter((person) => person.type === "TEACHER").length > 0 && (
            <Stack pt={16} spacing={8}>
              <Heading fontWeight="black" fontSize="3xl">
                Docent
              </Heading>
              <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={16} minH="150px">
                {event.eventPeople
                  .filter((person) => person.type === "TEACHER")
                  .map((person) => (
                    <Person key={person.id} person={person} />
                  ))}
              </SimpleGrid>
            </Stack>
          )}
          {event.eventPeople.filter((person) => person.type === "HOST").length > 0 && (
            <Stack pt={16} spacing={8}>
              <Heading fontWeight="black" fontSize="3xl">
                Hosts
              </Heading>
              <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={16} minH="150px">
                {event.eventPeople
                  .filter((person) => person.type === "HOST")
                  .map((person) => (
                    <Person key={person.id} person={person} />
                  ))}
              </SimpleGrid>
            </Stack>
          )}
          <EventAttendees event={event} onShowMore={onFetchMore} count={event.attendeeCount} />
        </Stack>
      </Limiter>
    </>
  )
}

EventDetail.getLayout = (page: React.ReactElement) => <Layout>{page}</Layout>

interface PersonProps {
  person: EventPersonFragment
}

function Person({ person }: PersonProps) {
  const modalProps = useDisclosure()

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      modalProps.onOpen()
    }
  }

  return (
    <Box>
      <Head>
        <title>Kunstbende - Agenda</title>
      </Head>
      <Stack  
        align="center"
        cursor="pointer"
        onClick={modalProps.onOpen}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-label={`Open details voor ${person.name}`}
        _focus={{ outline: "2px solid", outlineColor: "yellow.500", outlineOffset: "2px" }}
      >
        <Image
          alt="Afbeelding van gebeurtenispersoon"
          borderRadius="xl"
          src={transformImage(person.imageUrl, "h_450")}
          minW="220px"
          h="220px"
          objectFit="cover"
          cursor="pointer"
        />
        <Text textAlign="center" fontSize="md" my={1} noOfLines={2}>
          {person.bio}
        </Text>
      </Stack>
      <Modal {...modalProps}>
        <Stack align="center">
          <Image
            alt="Afbeelding van gebeurtenispersoon"
            mt={8}
            borderRadius="xl"
            src={transformImage(person.imageUrl, "h_450")}
            minW="220px"
            h="220px"
            objectFit="cover"
          />
          <Text fontSize="2xl" fontWeight="extrabold" my={3}>
            {person.name}
          </Text>
          <Text fontSize="xl" my={3}>
            {person.bio}
          </Text>
        </Stack>
      </Modal>
    </Box>
  )
}
